import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as GavelIcon,
  AccountBalance as CourtIcon,
  DateRange as DateIcon,
  Star as StarIcon,
  KeyboardArrowRight as ArrowIcon,
  Info as InfoIcon
} from '@mui/icons-material';
// Date picker imports commented out - package not installed
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { cs } from 'date-fns/locale';

interface JurisprudenceCase {
  id: number;
  case_number: string;
  court_type: string;
  court_name: string;
  decision_date: string;
  case_title: string;
  case_summary: string;
  full_decision_text?: string;
  legal_area?: string;
  keywords?: string;
  source_url?: string;
  relevance_score?: number;
  matched_terms?: string[];
}

interface SearchResponse {
  query: string;
  total_found: number;
  search_time_ms: number;
  cases: JurisprudenceCase[];
  ai_query_analysis?: string;
}

interface CaseDetail {
  case: JurisprudenceCase;
  related_cases: JurisprudenceCase[];
  ai_analysis: string;
  key_legal_points: string[];
  similar_cases_reasoning: string;
}

const COURT_TYPES = [
  { code: 'NSoud', name: 'Nejvyšší soud', color: '#1976d2' },
  { code: 'VOsoud', name: 'Vrchní soud', color: '#388e3c' },
  { code: 'TVsoud', name: 'Krajský/Okresní soud', color: '#f57c00' }
];

const LEGAL_AREAS = [
  { code: 'trestní', name: 'Trestní právo' },
  { code: 'občanské', name: 'Občanské právo' },
  { code: 'správní', name: 'Správní právo' },
  { code: 'obchodní', name: 'Obchodní právo' },
  { code: 'ústavní', name: 'Ústavní právo' },
  { code: 'ostatní', name: 'Ostatní oblasti' }
];

export default function JurisprudenceSearchPage() {
  const [query, setQuery] = useState('');
  const [selectedCourts, setSelectedCourts] = useState<string[]>(['NSoud', 'VOsoud', 'TVsoud']);
  const [selectedLegalAreas, setSelectedLegalAreas] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
  const [selectedCase, setSelectedCase] = useState<CaseDetail | null>(null);
  const [selectedMatchedTerms, setSelectedMatchedTerms] = useState<string[] | undefined>(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Zadejte prosím vyhledávací dotaz');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSearchResults(null);

    try {
      const searchParams = {
        query: query.trim(),
        court_types: selectedCourts,
        legal_areas: selectedLegalAreas.length > 0 ? selectedLegalAreas : undefined,
        date_from: dateFrom?.toISOString().split('T')[0],
        date_to: dateTo?.toISOString().split('T')[0],
        limit: 10
      };

      const response = await fetch('/api/v1/jurisprudence/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(searchParams)
      });

      if (!response.ok) {
        throw new Error(`Chyba při vyhledávání: ${response.status}`);
      }

      const data: SearchResponse = await response.json();
      setSearchResults(data);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Neočekávaná chyba při vyhledávání');
    } finally {
      setIsSearching(false);
    }
  };

  const handleCaseSelect = async (caseItem: JurisprudenceCase) => {
    setIsLoadingDetail(true);
    setSelectedCase(null);
    setSelectedMatchedTerms(caseItem.matched_terms);

    try {
      const response = await fetch(`/api/v1/jurisprudence/case/${caseItem.id}?context=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Chyba při načítání detailu: ${response.status}`);
      }

      const caseDetail: CaseDetail = await response.json();
      setSelectedCase(caseDetail);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chyba při načítání detailu případu');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const stripDiacritics = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}+/gu, '');
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const czCharClass = (ch: string) => {
    const map: Record<string, string> = { a:'aá', c:'cč', d:'dď', e:'eéě', i:'ií', n:'nň', o:'oó', r:'rř', s:'sš', t:'tť', u:'uúů', y:'yý', z:'zž' };
    const lower = ch.toLowerCase();
    const set = map[lower];
    return set ? `[${set}]` : escapeRegex(lower);
  };
  const normalizeBase = (term: string) => {
    const t = stripDiacritics(term.toLowerCase());
    const suffixes = ['ove','ami','emi','ach','ich','ych','eho','emu','iho','imu','ech','ost','nost','ace','aci','ou','em','om','um','am','im','y','a','u','e','i','o'];
    for (const suf of suffixes.sort((a,b)=>b.length-a.length)) {
      if (t.endsWith(suf) && t.length - suf.length >= 4) {
        return t.slice(0, t.length - suf.length);
      }
    }
    return t;
  };
  const termToPattern = (term: string) => {
    const base = normalizeBase(term);
    const chars = Array.from(base).map(czCharClass).join('');
    return `\\b${chars}(?:[a-zá-ž]{0,3})?\\b`;
  };
  const highlightText = (text: string, terms?: string[]) => {
    if (!text || !terms || terms.length === 0) return text;
    const uniq = Array.from(new Set(terms.filter(Boolean)));
    if (uniq.length === 0) return text;
    uniq.sort((a, b) => (b?.length || 0) - (a?.length || 0));
    const pattern = uniq.map(termToPattern).join('|');
    try {
      const re = new RegExp(`(${pattern})`, 'giu');
      const parts = text.split(re);
      return parts.map((part, idx) => (
        idx % 2 === 1 ? <mark key={idx}>{part}</mark> : <React.Fragment key={idx}>{part}</React.Fragment>
      ));
    } catch {
      return text;
    }
  };

  const handleCourtToggle = (courtCode: string) => {
    setSelectedCourts(prev =>
      prev.includes(courtCode)
        ? prev.filter(c => c !== courtCode)
        : [...prev, courtCode]
    );
  };

  const handleLegalAreaToggle = (areaCode: string) => {
    setSelectedLegalAreas(prev =>
      prev.includes(areaCode)
        ? prev.filter(a => a !== areaCode)
        : [...prev, areaCode]
    );
  };

  const getCourtColor = (courtType: string) => {
    const court = COURT_TYPES.find(c => c.code === courtType);
    return court?.color || '#757575';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  };

  return (
    // <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={cs}>
    <div>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h3" gutterBottom>
          <GavelIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Vyhledávání judikatury
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
          Inteligentní vyhledávání v databázích českých soudů (Nejvyšší soud, Vrchní soudy, Krajské soudy)
        </Typography>

        {/* Search Form */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: { md: 1 }, width: { xs: '100%', md: 'auto' } }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Co hledáte? (např. 'judikatura ohledně odsouzení za vraždu')"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Zadejte svůj právní dotaz..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={isSearching || !query.trim()}
                startIcon={isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ flex: 1 }}
              >
                {isSearching ? 'Hledám...' : 'Vyhledat'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtry
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          {showFilters && (
            <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Typy soudů:</Typography>
                  <FormGroup>
                    {COURT_TYPES.map((court) => (
                      <FormControlLabel
                        key={court.code}
                        control={
                          <Checkbox
                            checked={selectedCourts.includes(court.code)}
                            onChange={() => handleCourtToggle(court.code)}
                            sx={{ color: court.color }}
                          />
                        }
                        label={court.name}
                      />
                    ))}
                  </FormGroup>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Právní oblasti:</Typography>
                  <FormGroup>
                    {LEGAL_AREAS.map((area) => (
                      <FormControlLabel
                        key={area.code}
                        control={
                          <Checkbox
                            checked={selectedLegalAreas.includes(area.code)}
                            onChange={() => handleLegalAreaToggle(area.code)}
                          />
                        }
                        label={area.name}
                      />
                    ))}
                  </FormGroup>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Časové rozmezí:</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Od (YYYY-MM-DD)"
                      type="date"
                      size="small"
                      value={dateFrom?.toISOString().split('T')[0] || ''}
                      onChange={(e) => setDateFrom(e.target.value ? new Date(e.target.value) : null)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Do (YYYY-MM-DD)"
                      type="date"
                      size="small"
                      value={dateTo?.toISOString().split('T')[0] || ''}
                      onChange={(e) => setDateTo(e.target.value ? new Date(e.target.value) : null)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Search Results */}
        {searchResults && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Výsledky vyhledávání
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Nalezeno {searchResults.total_found} případů za {(searchResults.search_time_ms / 1000).toFixed(2)} s
              </Typography>

              {searchResults.ai_query_analysis && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">AI analýza dotazu:</Typography>
                  <Typography variant="body2">{searchResults.ai_query_analysis}</Typography>
                </Alert>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {searchResults.cases.map((caseItem) => (
                <Box key={caseItem.id}>
                  <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                        onClick={() => handleCaseSelect(caseItem)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {caseItem.case_number}
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            {highlightText(caseItem.case_title, caseItem.matched_terms)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {highlightText(caseItem.case_summary.substring(0, 300), caseItem.matched_terms)}
                            {caseItem.case_summary.length > 300 && '...'}
                          </Typography>
                        </Box>

                        {caseItem.relevance_score && (
                          <Box sx={{ ml: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Relevance
                            </Typography>
                            <Rating
                              value={caseItem.relevance_score}
                              precision={0.1}
                              readOnly
                              size="small"
                            />
                            <Typography variant="caption" display="block">
                              {(caseItem.relevance_score * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip
                          icon={<CourtIcon />}
                          label={caseItem.court_name}
                          size="small"
                          sx={{ backgroundColor: getCourtColor(caseItem.court_type), color: 'white' }}
                        />
                        <Chip
                          icon={<DateIcon />}
                          label={formatDate(caseItem.decision_date)}
                          size="small"
                          variant="outlined"
                        />
                        {caseItem.legal_area && (
                          <Chip
                            label={LEGAL_AREAS.find(a => a.code === caseItem.legal_area)?.name || caseItem.legal_area}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {caseItem.matched_terms && caseItem.matched_terms.length > 0 && (
                          <Chip
                            label={`Shody: ${caseItem.matched_terms.join(', ')}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </CardContent>

                    <CardActions>
                      <Button size="small" endIcon={<ArrowIcon />}>
                        Zobrazit detail
                      </Button>
                    </CardActions>
                  </Card>
                </Box>
              ))}
            </Box>

            {searchResults.cases.length === 0 && (
              <Alert severity="info">
                Nenalezeny žádné výsledky pro váš dotaz. Zkuste upravit vyhledávací kritéria.
              </Alert>
            )}
          </Paper>
        )}

        {/* Case Detail Dialog */}
        <Dialog
          open={!!selectedCase || isLoadingDetail}
          onClose={() => setSelectedCase(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          {isLoadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : selectedCase && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <GavelIcon />
                  <Box>
                    <Typography variant="h6">
                      {selectedCase.case.case_number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedCase.case.court_name} • {formatDate(selectedCase.case.decision_date)}
                    </Typography>
                  </Box>
                </Box>
              </DialogTitle>

              <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {highlightText(selectedCase.case.case_title, selectedMatchedTerms)}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {highlightText(selectedCase.case.case_summary, selectedMatchedTerms)}
                  </Typography>

                  {/* Keywords display */}
                  {selectedCase.case.keywords && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Klíčová slova:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedCase.case.keywords.split(',').map((keyword, index) => (
                          <Chip key={index} label={keyword.trim()} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Full Decision Text */}
                {selectedCase.case.full_decision_text && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GavelIcon />
                        <Typography variant="subtitle1">Úplný text rozhodnutí</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {selectedCase.case.full_decision_text}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Database Analysis (formerly AI Analysis) */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon />
                      <Typography variant="subtitle1">Analýza rozhodnutí</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      {selectedCase.ai_analysis}
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                {/* Key Legal Points */}
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon />
                      <Typography variant="subtitle1">Klíčové právní body</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {selectedCase.key_legal_points.map((point, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <ArrowIcon />
                          </ListItemIcon>
                          <ListItemText primary={point} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>

                {/* Related Cases */}
                {selectedCase.related_cases.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">
                        Související případy ({selectedCase.related_cases.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {selectedCase.similar_cases_reasoning}
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      {selectedCase.related_cases.map((relatedCase) => (
                        <Card key={relatedCase.id} sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle2">
                              {relatedCase.case_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {relatedCase.court_name} • {formatDate(relatedCase.decision_date)}
                            </Typography>
                            <Typography variant="body2">
                              {relatedCase.case_summary}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Source Link */}
                {selectedCase.case.source_url && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      href={selectedCase.case.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Zobrazit v původním zdroji
                    </Button>
                  </Box>
                )}
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setSelectedCase(null)}>
                  Zavřít
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    {/* </LocalizationProvider> */}
    </div>
  );
}
